import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from '@/app/App';
import '@/styles/globals.css';

const BASE = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Wait for MSW to be fully ready by polling a health-check endpoint.
 * This prevents a race condition where page data requests (e.g. /cases, /products)
 * arrive before the service worker can intercept them, causing the SPA's index.html
 * to be returned as API response instead of JSON.
 */
async function waitForMSWReady(maxRetries = 15): Promise<void> {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const res = await fetch(`${BASE}/health-check`);
      if (res.ok) return;
    } catch {
      // MSW not ready yet, retry
    }
    await new Promise((r) => setTimeout(r, 200));
  }
  console.warn('[MSW] Health check did not confirm readiness, proceeding anyway');
}

async function bootstrap(): Promise<void> {
  // Clean up stale MSW service workers from previous deployments
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        if (reg.active?.scriptURL?.includes('mockServiceWorker')) {
          await reg.unregister();
          console.info('[MSW] Unregistered stale service worker');
        }
      }
    } catch {
      // Silently ignore — cleanup is best-effort
    }
  }

  // Start MSW mock service worker in all environments (dev + production)
  try {
    const { worker } = await import('@/mocks/browser');
    console.info('[MSW] Starting Mock Service Worker');
    await worker.start({
      onUnhandledRequest: 'bypass',
      serviceWorker: {
        url: '/mockServiceWorker.js',
      },
    });

    // Wait for MSW to be ready before rendering
    await waitForMSWReady();
  } catch (error) {
    console.error('[MSW] Failed to start:', error);
  }

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found. Ensure index.html has a <div id="root"> element.');
  }

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

bootstrap();